import dayjs, { type Dayjs } from 'dayjs';

/**
 * Svenska allmänna helgdagar samt de tre aftnar som enligt 2 § lagen (1930:173)
 * om beräkning av lagstadgad tid likställs med helgdag (midsommarafton,
 * julafton och nyårsafton).
 *
 * Varför detta behövs: när en deklarations- eller betalningsdag infaller på en
 * lördag, söndag eller helgdag flyttas den fram till nästa vardag. Utan den
 * justeringen visar vi fel datum — t.ex. arbetsgivardeklarationen för augusti
 * 2026, där den 12 september är en lördag och rätt datum är måndag 14 september.
 */

/** Påskdagen enligt den anonyma gregorianska algoritmen (Meeus/Jones/Butcher). */
export function påskdagen(år: number): Dayjs {
  const a = år % 19;
  const b = Math.floor(år / 100);
  const c = år % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const månad = Math.floor((h + l - 7 * m + 114) / 31); // 3 = mars, 4 = april
  const dag = ((h + l - 7 * m + 114) % 31) + 1;
  return dayjs(new Date(år, månad - 1, dag));
}

/** Alla helgdagar för ett givet år, som "MM-DD"-nycklar. */
function helgdagarFörÅr(år: number): Set<string> {
  const nyckel = (d: Dayjs) => d.format('MM-DD');
  const påsk = påskdagen(år);

  const dagar = [
    dayjs(new Date(år, 0, 1)), // nyårsdagen
    dayjs(new Date(år, 0, 6)), // trettondedag jul
    påsk.subtract(2, 'day'), // långfredagen
    påsk, // påskdagen
    påsk.add(1, 'day'), // annandag påsk
    dayjs(new Date(år, 4, 1)), // första maj
    påsk.add(39, 'day'), // Kristi himmelsfärdsdag
    påsk.add(49, 'day'), // pingstdagen
    dayjs(new Date(år, 5, 6)), // nationaldagen
    midsommarafton(år),
    midsommarafton(år).add(1, 'day'), // midsommardagen
    allaHelgonsDag(år),
    dayjs(new Date(år, 11, 24)), // julafton
    dayjs(new Date(år, 11, 25)), // juldagen
    dayjs(new Date(år, 11, 26)), // annandag jul
    dayjs(new Date(år, 11, 31)), // nyårsafton
  ];

  return new Set(dagar.map(nyckel));
}

/** Midsommarafton = fredagen mellan 19 och 25 juni. */
function midsommarafton(år: number): Dayjs {
  let d = dayjs(new Date(år, 5, 19));
  while (d.day() !== 5) {
    d = d.add(1, 'day');
  }
  return d;
}

/** Alla helgons dag = lördagen mellan 31 oktober och 6 november. */
function allaHelgonsDag(år: number): Dayjs {
  let d = dayjs(new Date(år, 9, 31));
  while (d.day() !== 6) {
    d = d.add(1, 'day');
  }
  return d;
}

const cache = new Map<number, Set<string>>();

export function ärHelgdag(datum: Dayjs): boolean {
  const år = datum.year();
  if (!cache.has(år)) {
    cache.set(år, helgdagarFörÅr(år));
  }
  return cache.get(år)!.has(datum.format('MM-DD'));
}

export function ärVardag(datum: Dayjs): boolean {
  const veckodag = datum.day();
  return veckodag !== 0 && veckodag !== 6 && !ärHelgdag(datum);
}

/**
 * Flyttar fram ett datum till närmast följande vardag. Returnerar datumet
 * oförändrat om det redan är en vardag.
 */
export function nästaVardag(datum: Dayjs): Dayjs {
  let d = datum.startOf('day');
  while (!ärVardag(d)) {
    d = d.add(1, 'day');
  }
  return d;
}
